import {
  Select as AntSelect,
  SelectProps as AntSelectProps,
  theme,
} from 'antd';
import styled, { css } from 'styled-components';
import type { GlobalToken } from 'antd';
import {
  ComponentSize,
  getControlHeight,
  getBorderRadius,
} from '../../utils/tokenHelpers';
import { radius } from '../../theme/radius';

export type SelectStatus = 'default' | 'error';

export interface SelectProps extends Omit<AntSelectProps, 'status' | 'size'> {
  /** Select status for validation feedback */
  status?: SelectStatus;
  /** Label text */
  label?: string;
  /** Help text below the select */
  helpText?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Select size following Figma design system */
  size?: ComponentSize;
}

const getStatusStyles = (token: GlobalToken, status: SelectStatus) => {
  const statusMap = {
    default: css`
      &.ant-select:not(.ant-select-disabled) {
        .ant-select-selector {
          background: ${token.colorBgContainer};
          border-color: ${token.colorBorderSecondary};
        }

        &:hover .ant-select-selector {
          border-color: ${token.colorPrimaryHover};
        }

        &.ant-select-focused .ant-select-selector,
        &.ant-select-open .ant-select-selector {
          border-color: ${token.colorPrimary};
          box-shadow: 0 0 0 2px ${token.colorPrimaryBg};
        }
      }
    `,
    error: css`
      &.ant-select:not(.ant-select-disabled) {
        .ant-select-selector {
          background: ${token.colorErrorBg};
          border-color: ${token.colorError};
        }

        &:hover .ant-select-selector {
          border-color: ${token.colorErrorHover};
        }

        &.ant-select-focused .ant-select-selector,
        &.ant-select-open .ant-select-selector {
          border-color: ${token.colorError};
          box-shadow: 0 0 0 2px ${token.colorErrorBg};
        }
      }
    `,
  };
  return statusMap[status];
};

interface WrapperProps {
  $token: GlobalToken;
}

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: column;
  gap: ${({ $token }) => $token.marginXXS}px;
  width: 100%;
`;

const LabelWrapper = styled.label<WrapperProps>`
  display: flex;
  align-items: center;
  gap: ${({ $token }) => $token.marginXXS}px;
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $token }) => $token.fontSize}px;
  font-weight: 400;
  color: ${({ $token }) => $token.colorText};
`;

const RequiredMark = styled.span<WrapperProps>`
  color: ${({ $token }) => $token.colorError};
`;

interface HelpTextProps extends WrapperProps {
  $status: SelectStatus;
}

const HelpText = styled.span<HelpTextProps>`
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $token }) => $token.fontSizeSM}px;
  color: ${({ $token, $status }) =>
    $status === 'error' ? $token.colorError : $token.colorTextTertiary};
`;

interface SelectWrapperProps {
  $token: GlobalToken;
  $status: SelectStatus;
  $height: number;
  $borderRadius: number;
}

const SelectWrapper = styled.div<SelectWrapperProps>`
  .ant-select {
    width: 100%;
    // antd hardcodes .ant-select-single's height to controlHeight (the
    // "middle" size) since we don't forward our size prop to AntSelect —
    // without this the arrow, centered on this box, drifts off the
    // taller/shorter selector we render below.
    height: ${({ $height }) => $height}px;

    .ant-select-selector {
      font-family: ${({ $token }) => $token.fontFamily};
      font-size: ${({ $token }) => $token.fontSize}px;
      height: ${({ $height }) => $height}px;
      padding: ${({ $token }) =>
        `${$token.paddingXXS}px ${$token.paddingSM}px`};
      border-radius: ${({ $borderRadius }) => $borderRadius}px;
      border: ${({ $token }) => $token.lineWidth}px solid;
      transition: all ${({ $token }) => $token.motionDurationMid} ease;
    }

    .ant-select-selection-item,
    .ant-select-selection-placeholder {
      line-height: ${({ $height, $token }) =>
        $height - $token.paddingXXS * 2 - $token.lineWidth * 2}px;
    }

    .ant-select-selection-placeholder {
      color: ${({ $token }) => $token.colorTextTertiary};
    }

    .ant-select-arrow {
      color: ${({ $token }) => $token.colorTextSecondary};
    }

    &.ant-select-disabled {
      .ant-select-selector {
        background: ${({ $token }) => $token.colorBgLayout};
        border-color: ${({ $token }) => $token.colorBorderSecondary};
        color: ${({ $token }) => $token.colorTextDisabled};
      }
    }

    ${({ $token, $status }) => getStatusStyles($token, $status)}
  }
`;

/**
 * Select component based on Figma Design System
 *
 * Now uses Ant Design theme tokens for consistent styling and size support.
 *
 * @example
 * ```tsx
 * import { Select } from '@libertify/ui';
 *
 * <Select
 *   label="Country"
 *   placeholder="Select a country"
 *   options={[
 *     { value: 'us', label: 'United States' },
 *     { value: 'uk', label: 'United Kingdom' },
 *   ]}
 * />
 *
 * // Different sizes
 * <Select size="s" placeholder="Small select" options={[...]} />
 * <Select size="l" placeholder="Large select" options={[...]} />
 * ```
 */
export const Select = ({
  status = 'default',
  label,
  helpText,
  required,
  size = 'm',
  ...props
}: SelectProps) => {
  const { token } = theme.useToken();
  const height = getControlHeight(token, size);
  const borderRadius = getBorderRadius(token, size);

  const popupStyles = {
    root: {
      padding: `${token.paddingXXS}px`,
      borderRadius: `${radius.box}px`,
      boxShadow: token.boxShadowSecondary,
    },
  };

  return (
    <Wrapper $token={token}>
      {label && (
        <LabelWrapper $token={token}>
          {label}
          {required && <RequiredMark $token={token}>*</RequiredMark>}
        </LabelWrapper>
      )}
      <SelectWrapper
        $token={token}
        $status={status}
        $height={height}
        $borderRadius={borderRadius}
      >
        <AntSelect styles={{ popup: popupStyles }} {...props} />
      </SelectWrapper>
      {helpText && (
        <HelpText $token={token} $status={status}>
          {helpText}
        </HelpText>
      )}
    </Wrapper>
  );
};

export default Select;
